import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Checkbox,
  Icon,
  LoadingSpinner,
  Modal,
  ModalBody,
  ModalButtons,
  ModalHeader,
  SegmentedControl,
  TextField,
} from "@charcoal-ui/react";
import { loadConfig, searchListings, unlockWithPassphrase, type ListingCard } from "./api";

interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  text: string;
  listings: ListingCard[];
}

const AREAS = ["秋葉原", "池袋", "新宿", "渋谷"] as const;
const DEFAULT_AREAS = ["秋葉原", "池袋", "新宿"];
const SUGGESTIONS = ["池袋で処女っぽい人いる？", "秋葉原の未経験教えて", "新宿の体入いる？"] as const;
const WELCOME: ChatMessage = {
  id: "welcome",
  role: "assistant",
  text: "今夜どこに行く？下に書いて聞いてみて。",
  listings: [],
};

function formatHours(value: string | null): string {
  if (value === null || value.trim() === "") return "時間の記載なし";
  return value;
}

function ageLine(card: ListingCard): string {
  if (typeof card.declaredAge === "number") return `${String(card.declaredAge)}歳`;
  return "年齢記載なし";
}

function experienceLabel(card: ListingCard): string | null {
  const evidence = card.experienceEvidence ?? "";
  if (card.industryExperience === "inexperienced" || evidence.includes("未経験") || /処女|バージン|ヴァージン/u.test(evidence)) {
    if (/処女|バージン|ヴァージン/u.test(evidence)) return evidence;
    return /完全/.test(evidence) ? "業界完全未経験" : "業界未経験";
  }
  const newcomer = card.newcomerLabel ?? "";
  if (
    card.industryExperience !== "experienced" &&
    /体験入店|体入|デビュー|初日/u.test(newcomer)
  ) {
    return "経験の記載なし";
  }
  return null;
}

function ListingPhoto({ card }: { card: ListingCard }) {
  const [failed, setFailed] = useState(false);
  const showImage = !failed;
  return (
    <a
      className="photo"
      href={card.profileUrl}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`${card.publicName}の詳細ページ`}
    >
      {showImage ? (
        <img
          src={`/api/photo/${card.id}`}
          alt={card.publicName}
          onError={() => {
            setFailed(true);
          }}
        />
      ) : (
        <div className="placeholder">{card.publicName.slice(0, 1)}</div>
      )}
    </a>
  );
}

function ResultCard({ card }: { card: ListingCard }) {
  const experience = experienceLabel(card);
  const innocenceLabel = card.innocenceLabel ?? null;
  const innocenceLevel = card.innocenceLevel ?? null;
  const innocenceReasons = card.innocenceReasons ?? [];
  return (
    <article className="card">
      <ListingPhoto card={card} />
      <div className="meta">
        <h2>
          <a className="name-link" href={card.profileUrl} target="_blank" rel="noopener noreferrer">
            {card.publicName}
          </a>
        </h2>
        {innocenceLabel !== null && innocenceLevel !== null ? (
          <div className={`innocence innocence-${innocenceLevel}`}>
            <p className="innocence-label">{innocenceLabel}</p>
            {innocenceReasons.length > 0 ? (
              <p className="innocence-reasons">{innocenceReasons.join(" · ")}</p>
            ) : null}
          </div>
        ) : null}
        <p className="shop-name">{card.shopName}</p>
        <p className="hours">{formatHours(card.scheduleText)}</p>
        {card.firstDayLabel ? <p className="first-day">{card.firstDayLabel}</p> : null}
        <p className="age-line">{[card.area, ageLine(card)].filter(Boolean).join(" · ")}</p>
        <div className="badges">
          {card.newcomerLabel !== null && card.newcomerLabel !== "" ? (
            <Button type="button" size="S" disabled>
              {card.newcomerLabel}
            </Button>
          ) : null}
          {experience !== null ? (
            <Button type="button" size="S" disabled>
              {experience}
            </Button>
          ) : null}
          {card.isWorkingNow ? (
            <Button type="button" size="S" variant="Primary" disabled>
              出勤中
            </Button>
          ) : null}
        </div>
        <div className="card-actions">
          <Button
            component="a"
            href={card.profileUrl}
            target="_blank"
            rel="noopener noreferrer"
            variant="Primary"
            size="S"
          >
            詳細を見る
          </Button>
        </div>
      </div>
    </article>
  );
}

export function App() {
  const conversationId = useMemo(() => crypto.randomUUID(), []);
  const [gateReady, setGateReady] = useState(false);
  const [unlocked, setUnlocked] = useState(false);
  const [passphrase, setPassphrase] = useState("");
  const [gateError, setGateError] = useState<string | null>(null);
  const [unlocking, setUnlocking] = useState(false);
  const [adultConfirmed, setAdultConfirmed] = useState(
    () => localStorage.getItem("adult-confirmed") === "true",
  );
  const [areas, setAreas] = useState<string[]>(DEFAULT_AREAS);
  const [date, setDate] = useState("今日");
  const [newcomerOnly, setNewcomerOnly] = useState(true);
  const [inexperiencedOnly, setInexperiencedOnly] = useState(false);
  const [innocentOnly, setInnocentOnly] = useState(false);
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([WELCOME]);
  const transcriptEnd = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [turnstileSiteKey, setTurnstileSiteKey] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string>();
  const turnstileHost = useRef<HTMLDivElement>(null);
  const turnstileWidgetId = useRef<string>(undefined);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const config = await loadConfig();
        if (cancelled) return;
        setUnlocked(config.unlocked);
        setTurnstileSiteKey(config.turnstileSiteKey);
      } catch {
        if (!cancelled) setUnlocked(false);
      } finally {
        if (!cancelled) setGateReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!unlocked || !adultConfirmed || !turnstileSiteKey) return;
    let cancelled = false;
    const script = document.createElement("script");
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.defer = true;
    script.addEventListener("load", () => {
      if (cancelled || turnstileHost.current === null || window.turnstile === undefined) return;
      turnstileWidgetId.current = window.turnstile.render(turnstileHost.current, {
        sitekey: turnstileSiteKey,
        theme: "dark",
        size: "flexible",
        callback: (token) => {
          setTurnstileToken(token);
        },
      });
    });
    document.head.append(script);
    return () => {
      cancelled = true;
    };
  }, [adultConfirmed, turnstileSiteKey, unlocked]);

  const spokenPrompt = useCallback(() => {
    const typed = query.trim();
    if (typed !== "") return typed;
    const parts = [areas.join("・"), date];
    if (innocentOnly) parts.push("処女っぽい");
    if (inexperiencedOnly) parts.push("業界完全未経験");
    if (newcomerOnly) parts.push("体入・新人");
    return `${parts.join("、")}を教えて`;
  }, [areas, date, inexperiencedOnly, innocentOnly, newcomerOnly, query]);

  const buildQuery = useCallback(() => {
    const typed = query.trim();
    if (typed !== "") return typed;
    const areaText = areas.length === 1 ? (areas[0] ?? "") : areas.join("・");
    const newcomer = newcomerOnly ? "体験入店・新人の" : "";
    const inexperienced = inexperiencedOnly ? "業界完全未経験の" : "";
    const innocent = innocentOnly ? "処女っぽい" : "";
    return `${areaText}で${date}出勤する${inexperienced}${innocent}${newcomer}人を24人教えて`;
  }, [areas, date, inexperiencedOnly, innocentOnly, newcomerOnly, query]);

  const runSearch = useCallback(
    async (nextMessage?: string) => {
      if (busy) return;
      const typed = nextMessage?.trim() ?? "";
      const asked = typed !== "" ? typed : spokenPrompt();
      const message = typed !== "" ? typed : buildQuery();
      const assistantId = crypto.randomUUID();
      setBusy(true);
      setMessages((current) => [
        ...current,
        { id: crypto.randomUUID(), role: "user", text: asked, listings: [] },
        { id: assistantId, role: "assistant", text: "探しています…", listings: [] },
      ]);
      try {
        const result = await searchListings({
          conversationId,
          message,
          newcomerOnly,
          inexperiencedOnly,
          innocentOnly,
          turnstileToken,
        });
        const reply =
          result.summary ||
          (result.listings.length === 0
            ? "見つかりませんでした。条件を変えて聞いてみてください。"
            : `${String(result.listings.length)}人見つかりました。`);
        setMessages((current) =>
          current.map((item) =>
            item.id === assistantId
              ? { ...item, text: reply, listings: result.listings }
              : item,
          ),
        );
      } catch (error) {
        const reply = error instanceof Error ? error.message : "うまく聞けませんでした。";
        setMessages((current) =>
          current.map((item) => (item.id === assistantId ? { ...item, text: reply } : item)),
        );
      } finally {
        setBusy(false);
        setQuery("");
        inputRef.current?.focus();
        if (window.turnstile !== undefined && turnstileWidgetId.current !== undefined) {
          window.turnstile.reset(turnstileWidgetId.current);
          setTurnstileToken(undefined);
        }
      }
    },
    [
      buildQuery,
      busy,
      conversationId,
      inexperiencedOnly,
      innocentOnly,
      newcomerOnly,
      spokenPrompt,
      turnstileToken,
    ],
  );

  useEffect(() => {
    transcriptEnd.current?.scrollIntoView({ block: "end" });
  }, [messages]);

  useEffect(() => {
    if (unlocked && adultConfirmed) inputRef.current?.focus();
  }, [adultConfirmed, unlocked]);

  const submitPassphrase = async () => {
    if (unlocking) return;
    setUnlocking(true);
    setGateError(null);
    try {
      await unlockWithPassphrase(passphrase);
      setUnlocked(true);
      setPassphrase("");
    } catch (error) {
      setGateError(error instanceof Error ? error.message : "入れませんでした。");
    } finally {
      setUnlocking(false);
    }
  };

  const toggleArea = (area: string) => {
    setAreas((current) => {
      if (current.includes(area)) {
        return current.length === 1 ? current : current.filter((item) => item !== area);
      }
      return [...current, area];
    });
  };

  const hasUserTurn = messages.some((message) => message.role === "user");

  if (!gateReady) {
    return (
      <div className="gate">
        <LoadingSpinner size={32} padding={24} transparent />
      </div>
    );
  }

  if (!unlocked) {
    return (
      <div className="gate">
        <form
          className="gate-card"
          onSubmit={(event) => {
            event.preventDefault();
            void submitPassphrase();
          }}
        >
          <div className="brand gate-brand">
            <Icon name="24/LockLock" />
            <span className="brand-copy">
              <strong>夜ふかし</strong>
              <small>合言葉を入れて入る</small>
            </span>
          </div>
          <TextField
            label="合言葉"
            type="password"
            value={passphrase}
            onChange={setPassphrase}
            maxLength={100}
            placeholder="合言葉"
            autoComplete="off"
            autoCapitalize="off"
            spellCheck={false}
            disabled={unlocking}
          />
          {gateError !== null ? <p className="gate-error">{gateError}</p> : null}
          <Button type="submit" variant="Primary" fullWidth disabled={unlocking || passphrase.trim() === ""}>
            入る
          </Button>
        </form>
      </div>
    );
  }

  return (
    <div className="page">
      <header className="chrome">
        <div className="topbar">
          <a className="brand" href="/" aria-label="夜ふかし ホーム">
            <Icon name="24/CommentFill" />
            <span className="brand-copy">
              <strong>夜ふかし</strong>
              <small>チャットで聞いてみる</small>
            </span>
          </a>
          <Button type="button" size="S" variant="Overlay" disabled>
            18+
          </Button>
        </div>
        <div className="filters" aria-label="絞り込み">
          <div className="chips">
            {AREAS.map((area) => {
              const on = areas.includes(area);
              return (
                <Button
                  key={area}
                  type="button"
                  size="S"
                  variant={on ? "Primary" : "Default"}
                  isActive={on}
                  aria-pressed={on}
                  onClick={() => {
                    toggleArea(area);
                  }}
                >
                  {area}
                </Button>
              );
            })}
          </div>
          <SegmentedControl
            name="出勤日"
            value={date}
            data={["今日", "明日"]}
            uniformSegmentWidth
            onChange={setDate}
          />
          <div className="filter-checks">
            <Checkbox checked={newcomerOnly} onChange={setNewcomerOnly}>
              体入・新人だけ
            </Checkbox>
            <Checkbox checked={inexperiencedOnly} onChange={setInexperiencedOnly}>
              業界完全未経験だけ
            </Checkbox>
            <Checkbox checked={innocentOnly} onChange={setInnocentOnly}>
              処女っぽい人だけ
            </Checkbox>
          </div>
        </div>
      </header>

      <main className="thread" aria-live="polite" aria-busy={busy} aria-label="チャット">
        {messages.map((message, index) => (
          <div key={message.id} className={`turn ${message.role}`}>
            <p className={`bubble ${message.role}`}>
              {busy && index === messages.length - 1 && message.role === "assistant" ? (
                <LoadingSpinner size={20} padding={0} transparent />
              ) : null}
              {message.text}
            </p>
            {message.listings.length > 0 ? (
              <div className="cards" aria-label="見つかった人">
                {message.listings.map((card) => (
                  <ResultCard key={`${card.profileUrl}:${card.publicName}`} card={card} />
                ))}
              </div>
            ) : null}
          </div>
        ))}
        {hasUserTurn ? null : (
          <div className="suggestions" aria-label="よくある聞き方">
            {SUGGESTIONS.map((suggestion) => (
              <Button
                key={suggestion}
                type="button"
                size="S"
                variant="Overlay"
                disabled={busy}
                onClick={() => {
                  void runSearch(suggestion);
                }}
              >
                {suggestion}
              </Button>
            ))}
          </div>
        )}
        <div ref={transcriptEnd} />
      </main>

      <form
        className="composer"
        onSubmit={(event) => {
          event.preventDefault();
          void runSearch();
        }}
      >
        <TextField
          ref={inputRef}
          label="メッセージ"
          showLabel={false}
          value={query}
          onChange={setQuery}
          maxLength={500}
          placeholder="メッセージを入力"
          autoComplete="off"
          enterKeyHint="send"
          disabled={busy}
        />
        <Button type="submit" variant="Primary" disabled={busy}>
          送る
        </Button>
        <div ref={turnstileHost} className="turnstile" />
      </form>

      <Modal
        title="18歳以上ですか？"
        isOpen={!adultConfirmed}
        onClose={() => undefined}
        isDismissable={false}
      >
        <ModalHeader />
        <ModalBody>
          <p>18歳以上の方だけ利用できます。</p>
          <ModalButtons>
            <Button
              variant="Primary"
              fullWidth
              onClick={() => {
                localStorage.setItem("adult-confirmed", "true");
                setAdultConfirmed(true);
              }}
            >
              18歳以上です
            </Button>
            <a className="age-exit" href="https://www.yahoo.co.jp/">
              退出する
            </a>
          </ModalButtons>
        </ModalBody>
      </Modal>
    </div>
  );
}
